import {
  type InfiniteData,
  useMutation,
  useQueryClient
} from '@tanstack/react-query'
import { type ApiResponseError, fetchApiWithToken } from '~/api/utils'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import type { ActionsCollectionResponse } from '~/domains/actions/queries/actions'
import type { Action } from '~/domains/actions/types'
import { toast } from '~/utils'
import { toISOLocal } from '~/utils/date'

type UpdateTaskPayload = {
  taskId: number
  isCompleted?: boolean
  isCancelled?: boolean
  /** Sent with a status change; the server defaults to now when omitted. */
  completionDate?: Date
  notes?: string
  subject?: string
  scheduledDate?: Date | null
  assignedUserId?: number
  applicantIds?: number[]
  /** Quick completion from a card: the hook shows the toasts itself (the card unmounts before the callbacks). */
  quickDone?: { title: string }
}

const isInfiniteActions = (
  data: unknown
): data is InfiniteData<ActionsCollectionResponse> =>
  typeof data === 'object' &&
  data !== null &&
  'pages' in data &&
  Array.isArray(data.pages)

const removeTaskFromLists = (
  data: InfiniteData<ActionsCollectionResponse>,
  taskId: number
): InfiniteData<ActionsCollectionResponse> => ({
  ...data,
  pages: data.pages.map((page) => ({
    ...page,
    'hydra:member': page['hydra:member'].filter((task) => task.id !== taskId),
    'hydra:totalItems': Math.max((page['hydra:totalItems'] ?? 1) - 1, 0)
  }))
})

export const useUpdateTaskMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    Action,
    ApiResponseError,
    UpdateTaskPayload,
    { previousLists: [readonly unknown[], unknown][] }
  >({
    mutationFn: ({ taskId, ...payload }) =>
      fetchApiWithToken({
        method: 'PATCH',
        url: `/v1/actions/${taskId}`,
        data: {
          isCompleted: payload.isCompleted,
          isCancelled: payload.isCancelled,
          completionDate: payload.completionDate
            ? toISOLocal(payload.completionDate)
            : undefined,
          notes: payload.notes,
          subject: payload.subject,
          datetime:
            payload.scheduledDate === undefined
              ? undefined
              : payload.scheduledDate
                ? toISOLocal(payload.scheduledDate)
                : null,
          user: payload.assignedUserId
            ? `/v1/users/${payload.assignedUserId}`
            : undefined
        }
      }),
    onMutate: async (payload) => {
      const isClosing =
        payload.isCompleted !== undefined || payload.isCancelled === true
      if (isClosing) {
        // A refetch in flight would put the closed task back in the lists
        await queryClient.cancelQueries({ queryKey: ['tasks'] })
      }
      const previousLists = queryClient.getQueriesData({ queryKey: ['tasks'] })
      if (isClosing) {
        queryClient.setQueriesData({ queryKey: ['tasks'] }, (data: unknown) =>
          isInfiniteActions(data)
            ? removeTaskFromLists(data, payload.taskId)
            : data
        )
      }
      return { previousLists }
    },
    onSuccess: (task, payload) => {
      if (payload.quickDone) {
        toast('Tâche terminée', payload.quickDone.title, ToastType.Success)
      }
      // The PATCH response keeps the public contract (IRIs), the aside reloads the full task
      queryClient.invalidateQueries({ queryKey: ['task', task.id] })
      for (const applicantId of payload.applicantIds ?? []) {
        queryClient.invalidateQueries({
          queryKey: ['applicant', applicantId, 'history']
        })
      }
    },
    onError: (_error, payload, context) => {
      if (payload.quickDone) {
        toast(
          'Une erreur est survenue',
          "La tâche n'a pas pu être marquée comme faite",
          ToastType.Danger
        )
      }
      for (const [queryKey, data] of context?.previousLists ?? []) {
        queryClient.setQueryData(queryKey, data)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['actions'] })
    }
  })
}
